import {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useInsertionEffect,
  useMemo,
  useRef,
} from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

  const defaultEnableProps = {
      enableExpandAll: false,
      enableExpanding: false,
      enablePagination: false,
      enableTopToolbar: true,
      enableBottomToolbar: true,
      enableColumnActions: false,
      enableColumnFilters: false,
      enableHiding: false,
      enableSorting: false,
      enableEditing: true,
      enableRowActions: true,
      positionActionsColumn: "last",
      positionCreatingRow: "bottom",
      memoMode: "cells",
      layoutMode: "grid",
      createDisplayMode: "row",
      editDisplayMode: "table",
    }

/**
 * @template {import('material-react-table').MRT_RowData} TData
 */

/**
 * @typedef {import('material-react-table').MRT_TableOptions<TData>} MRTTableOptions
 * @typedef {import('material-react-table').MRT_ColumnDef<TData>} ColumnDef
 */

/**
 * @template {import('material-react-table').MRT_RowData} TData
 * @typedef {{
 *   scrollX: React.MutableRefObject<number>,
 *   tableOptions: {
 *     coreProps: Pick<MRTTableOptions, 'columns' | 'data' | 'getRowId'> & Partial<MRTTableOptions>,
 *     enableProps?: Partial<MRTTableOptions>,
 *     renderProps?: Partial<MRTTableOptions>,
 *     muiProps?: Pick<MRTTableOptions,
 *       'muiTablePaperProps' |
 *       'muiTableContainerProps' |
 *       'muiTableHeadProps' |
 *       'muiTableHeadRowProps' |
 *       'muiTableHeadCellProps' |
 *       'muiTableBodyCellProps' |
 *       'muiTableBodyRowProps' |
 *       'muiTableProps'
 *     >
 *   }
 * }} CustomTableProps
 */

const baseFontFamily =
  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji"';

/**
 * @template {import('material-react-table').MRT_RowData} TData
 * @type {import('react').ForwardRefExoticComponent<
 *   CustomTableProps<TData> & React.RefAttributes<{
 *     scrollToStart: () => void,
 *     scrollToEnd: () => void,
 *     getTableState: () => any[],
 *   }>
 * >}
 */
const CustomDesignedTable = forwardRef(function CustomDesignedTable(props, ref) {
  const { scrollX, tableOptions } = props;
  const tableContainerRef = useRef(null);

  const {
    coreProps,
    enableProps = {},
    renderProps = {},
    muiProps = {},
  } = tableOptions;

  useInsertionEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollLeft = scrollX?.current;
    }
  }, [coreProps.data]);

  const handleScroll = useCallback(() => {
    if (tableContainerRef.current) {
      scrollX.current = tableContainerRef.current?.scrollLeft;
    }
  }, [scrollX]);

  const mergedTableOptions = useMemo(() => {
    const {
      columns,
      data,
      getRowId,
      ...restCore
    } = coreProps;

    return {
      ...defaultEnableProps,
      ...enableProps,
      ...renderProps,
      ...restCore,
      columns,
      data,
      localization: {
        rowsPerPage: <p className="text-sm font-sans">Per Page</p>,
        noRecordsToDisplay:<div className="h-full w-full flex justify-center items-center font-sans text-sm">No records to display</div>
      },
      getRowId,
       state: {
        ...renderProps?.state, // this includes isLoading
      },
      muiTableBodyProps:{
        ...muiProps?.muiTableBodyProps,
      },
      muiTableContainerProps: {
        ref: tableContainerRef,
        onScroll: handleScroll,
        sx: {
          fontFamily: baseFontFamily,
          "&::-webkit-scrollbar": { height: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: 'hsl(var(--scroll-thumb-primary))',
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: 'hsl(var(--scroll-track-primary))' },
          scrollbarColor: "hsl(var(--scroll-thumb-primary)) hsl(var(--scroll-track-primary))",
          scrollbarWidth: "thin",
          // Force aqua blue hover color
          "& tbody .MuiTableRow-root:hover": {
            backgroundColor: "hsl(var(--table-hover)) !important",
          },
          "& tbody .MuiTableRow-root:hover td:after": {
            backgroundColor: "hsl(var(--table-hover)) !important",
          },
        },
        ...muiProps?.muiTableContainerProps,
      },
      muiTablePaperProps: ({ table }) => ({
        elevation: 0,
        style: {
          zIndex: table.getState().isFullScreen ? 2000 : undefined,
        },
        className:'font-sans !border !border-form-border !rounded-md',
        // sx: {
        //   border: "1px solid hsl(var(--form-border))",
        //   borderRadius: "8px",
        // },
        ...muiProps?.muiTablePaperProps,
      }),
      muiDetailPanelProps: {
        sx: {
          "& .MuiCollapse-root": {
            fontFamily: baseFontFamily,
            width: "100%",
          },
        },
        ...muiProps?.muiDetailPanelProps,
      },
      muiExpandButtonProps: ({ row, table }) => {
        const isExpanded = row.getIsExpanded?.();
        return {
          disabled: isExpanded && (row?.original?.band == -1 && (!row?.original?.actual_rate || !row?.original?.hourly_cost)),
          ...(muiProps?.muiTableHeadCellProps?.({ row }) || {}),
        }
      },

      muiTableBodyCellProps: (row) => ({
        className:'font-sans !border-none text-sm',
        sx: {
          padding: "4px 8px",
          fontSize: "0.875rem",
          fontFamily: baseFontFamily,
          // border: "none",
          "&:focus, &:focus-within, &:hover": {
            outline: "none",
            boxShadow: "none",
          },
        },
        ...(typeof muiProps?.muiTableBodyCellProps === "function"
          ? muiProps.muiTableBodyCellProps(row)
          : muiProps?.muiTableBodyCellProps),
      }),
      muiTableBodyRowProps: (row) => ({
        className: `hover:hsl(var(--table-hover)) ${row?.row?.index !== (data.length - 1) && "border-b border-border"} `,
        sx: {
          "&:hover": {
            backgroundColor: "hsl(var(--table-hover))!important",
          },
          "&:hover td:after": {
            backgroundColor: "hsl(var(--table-hover)) !important",
          },
        },
        ...(typeof muiProps?.muiTableBodyRowProps === "function"
          ? muiProps.muiTableBodyRowProps(row)
          : muiProps?.muiTableBodyRowProps),
      }),
      muiTableHeadCellProps: ({ column }) => {
        const { sx = {}, ...rest} = typeof muiProps?.muiTableHeadCellProps === "function" ? muiProps?.muiTableHeadCellProps?.({ column }) || {} : muiProps?.muiTableHeadCellProps || {};
        return{
        title: column.columnDef.header?.toString(),
        sx: {
          display: "flex",
          alignItems: "center", 
          border: "none",
          justifyContent: "center",
          fontFamily: baseFontFamily,
          padding: "4px 8px",
          ".Mui-TableHeadCell-Content-Wrapper:hover": {
            textOverflow: "ellipsis",
          },
          ".Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: 'hsl(var(--muted-foreground))',
            fontWeight: 500,
            marginLeft:column.columnDef.meta?.isRequired ? '12px' : 0
          },
            ...(column.id === "mrt-row-actions" && {
            height: "100%",
            minHeight: "100%",
            paddingTop: "4px",
            paddingBottom: "4px",
          }),
          "&::after": column.columnDef.meta?.isRequired
            ? {
                content: '" * "',
                color: "red",
                fontWeight: "bold",
                position: "absolute",
                // right: 4 ,
                left: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "inherit",
                paddingLeft: "2px",
              }
            : undefined,
            ...sx
        },
        ...rest,
      }},
      muiTableHeadProps: {
        className: 'font-sans bg-primary/10 border-b border-form-border',
        ...muiProps?.muiTableHeadProps,
      },
      muiTableHeadRowProps: {
        sx: {
          boxShadow: "none",
          background: "unset",
        },
        ...muiProps?.muiTableHeadRowProps,
      },
      muiTopToolbarProps: {
        sx: {
          borderBottom: "1px solid hsl(214.3 31.8% 91.4%)",
        },
      },
      muiTableProps: {
        sx: {
          borderCollapse: "separate",
          fontFamily: baseFontFamily,
        },
        ...muiProps?.muiTableProps,
      },
      muiPaginationProps: {
        color: "primary",
        className: "page",
        showFirstButton: false,
        showLastButton: false,
        showRowsPerPage: coreProps.showRowsPerPage,
        size: "small",
        variant: "outlined",
        sx: {
          boxShadow: "none",
          fontSize: "14px",
          "& .MuiFormLabel-root": {
            fontSize: "14px",
          },
        },
        SelectProps: {
          sx: {
            "& .MuiSelect-select": {
              padding: "4px 12px",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: 500,
              border: "1px solid hsl(var(--card-border))",
              color: "hsl(var(--form-label))",
              lineHeight: 1.2,
              minHeight: "unset",
            },
            "& .MuiInputBase-root": {
              fontSize: "12px",
              height: "30px",
            },
            "& .MuiSvgIcon-root": {
              fontSize: "16px",
            },
          },
          ...muiProps?.muiPaginationProps?.SelectProps,
        },
        sx: {
          "& .MuiTablePagination-toolbar": {
            minHeight: "40px",
            padding: "0 8px",
          },
          "& .MuiTablePagination-selectLabel": {
            fontSize: "12px",         // ✅ reduces 'Rows per page'
            marginRight: "6px",       // optional spacing
          },
          "& .MuiTablePagination-displayedRows": {
            fontSize: "12px",
          },
          "& .MuiInputBase-root": {
            fontSize: "12px",

          },
        },
        rowsPerPageOptions: coreProps.rowsPerPageOptions ? coreProps.rowsPerPageOptions : [2, 5, 10, 15, 20, 30, 50],
      },
      paginationDisplayMode: "pages",
    };
  }, [coreProps, enableProps, renderProps, muiProps, handleScroll]);

  const table = useMaterialReactTable(mergedTableOptions);

  useImperativeHandle(
    ref,
    () => ({
      scrollToStart: () => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft = 0;
        }
      },
      scrollToEnd: () => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollLeft =
            tableContainerRef.current.scrollWidth;
        }
      },
      getTableState: () => table.getRowModel().rows,
      getTable: () => table,
    }),
    [table]
  );

  return <div className="w-full"><MaterialReactTable table={table} /></div>;
});

export default memo(CustomDesignedTable);
